import React, { useState, useCallback } from 'react';
import { DropdownMenuItem } from '../../ui/dropdown-menu';
import { useLazyCommand, useDomainEvent, useEvent } from '@soap/modules';
import { Archive, X } from 'lucide-react';

interface ArchiveUserDialogProps {
  tenantUserId: string;
  tenantUser: string;
}

export function ArchiveUserDialog({ tenantUserId, tenantUser }: ArchiveUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendCommand] = useLazyCommand('C242v1_ArchiveTeacher');
  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });

  const close = useCallback(() => {
    setOpen(false);
    triggerCloseContextMenu();
  }, [triggerCloseContextMenu]);

  useEvent({
    eventName: "E197v1_OrganisationMembersUpdated",
    onEventReceived: () => {
      if (submitting) {
        setSubmitting(false);
        close();
      }
    }
  }, [submitting, close]);

  const handleConfirm = () => {
    setSubmitting(true);
    sendCommand({
      $type: 'C242v1_ArchiveTeacher',
      C242_UserId: tenantUserId
    });
  };

  if (!open) {
    return (
      <DropdownMenuItem
        className="w-full justify-start"
        data-dialog-item
        onClick={() => setOpen(true)}
      >
        <Archive className="w-4 h-4 mr-2" />
        Archive
      </DropdownMenuItem>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">Archive User</h2>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={submitting}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              Please confirm you want to archive this user <strong>{tenantUser}</strong>?
            </p>
            <div className="bg-action-blue/10 p-4 rounded-lg border border-action-blue/30">
              <h4 className="font-medium text-action-blue-dark mb-2">Archived Users</h4>
              <ul className="ml-4 space-y-1">
                <li className="text-sm text-action-blue-dark">• cannot login</li>
                <li className="text-sm text-action-blue-dark">• do not count towards your sub-account limit</li>
                <li className="text-sm text-action-blue-dark">• can be unarchived if you have available sub-accounts</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-5">
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Archiving...' : 'Archive'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
